import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import { AppShell } from "../components/AppShell";
import { PaginationBar, usePagination } from "../components/Pagination";
import {
  fetchProfessionalBilling,
  retryInvoiceEmail,
  downloadInvoicePdf,
} from "../api/billing";
import { Toast } from "../components/Toast";
import { C, font } from "../styles/tokens";
import { formatRupees } from "../utils/money";
import { InlineIcon } from "../components/InlineIcon";

const PRICES = {
  PROFESSIONAL_MEMBERSHIP: 18000, // paise
};

export function BillingPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { localUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [toast, setToast] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const billing = await fetchProfessionalBilling();
      setData(billing);
      setError(null);
    } catch (err) {
      setError(err.message ?? "Could not load billing");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  const payments = data?.payments ?? [];
  const pager = usePagination(payments, 10);

  async function handlePayMembership() {
    navigate("/checkout", {
      state: {
        type: "PROFESSIONAL_MEMBERSHIP",
        provider: "CASHFREE",
      },
    });
  }

  return (
    <AppShell>
      <style>{`
        /* From Uiverse.io by vinodjangid07 */
        .Btn {
          width: 44px;
          height: 44px;
          border: 2px solid rgb(214, 214, 214);
          border-radius: 12px;
          background-color: rgb(255, 255, 255);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          position: relative;
          transition-duration: 0.3s;
          box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.11);
          flex-shrink: 0;
        }

        .svgIcon {
          fill: rgb(70, 70, 70);
          width: 14px;
          height: 14px;
        }

        .icon2 {
          width: 16px;
          height: 4px;
          border-bottom: 2px solid rgb(70, 70, 70);
          border-left: 2px solid rgb(70, 70, 70);
          border-right: 2px solid rgb(70, 70, 70);
          margin-top: 1px;
        }

        .Btn:hover {
          background-color: rgb(51, 51, 51);
          border-color: rgb(51, 51, 51);
          transition-duration: 0.3s;
        }

        .Btn:hover .icon2 {
          border-bottom: 2px solid rgb(235, 235, 235);
          border-left: 2px solid rgb(235, 235, 235);
          border-right: 2px solid rgb(235, 235, 235);
        }

        .Btn:hover .svgIcon {
          fill: rgb(255, 255, 255);
          animation: slide-in-top 1s linear infinite;
        }

        @keyframes slide-in-top {
          0% {
            transform: translateY(-8px);
            opacity: 0;
          }

          100% {
            transform: translateY(0px);
            opacity: 1;
          }
        }
      `}</style>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 16px" }}>
        Billing
      </h1>

      {error && (
        <div style={{ ...cardStyle, color: C.red, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {localUser?.requiresManualApproval && (
        <div
          style={{
            marginBottom: 16,
            padding: "12px 16px",
            background: "#FEF3C7",
            border: "1px solid #F59E0B",
            borderRadius: 8,
            color: "#92400E",
            fontSize: 13,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>
            <InlineIcon name="warning" size={18} />
          </span>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              Manual review required
            </div>
            <div>
              A platform administrator reviews your registration. Full platform
              capabilities and public badges are available once it is approved.
            </div>
          </div>
        </div>
      )}

      {/* Membership card */}
      <MembershipCard
        membership={data?.membership}
        loading={loading}
        onPay={handlePayMembership}
        paying={paying}
        payError={payError}
        isMobile={isMobile}
        onDownloadInvoice={() => {
          const subPayment =
            payments.find(
              (p) =>
                p.type === "PROFESSIONAL_MEMBERSHIP" ||
                p.type === "ORGANISATION_SUBSCRIPTION" ||
                p.type === "INSTITUTION_SUBSCRIPTION",
            ) || payments[0];
          if (subPayment) {
            setSelectedInvoice(subPayment);
          } else {
            setToast({
              message: "No subscription payment record found.",
              type: "error",
            });
          }
        }}
      />

      {/* Payment history */}
      <section
        style={{ ...cardStyle, padding: isMobile ? 16 : 24, marginBottom: 16 }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>
          Payment history
        </h2>
        {loading ? (
          <div style={{ fontSize: 13, color: C.textMed }}>Loading...</div>
        ) : !data?.payments?.length ? (
          <div style={{ fontSize: 13, color: C.textMed, padding: "8px 0" }}>
            No payments yet.
          </div>
        ) : (
          <div>
            {pager.pageItems.map((p, i) => (
              <PaymentRow
                key={p.id}
                payment={p}
                showDivider={i > 0}
                isMobile={isMobile}
                onClick={() => setSelectedPayment(p)}
                onOpenInvoice={(e) => {
                  e.stopPropagation();
                  setSelectedInvoice(p);
                }}
              />
            ))}
            <PaginationBar
              pager={pager}
              itemLabel="payments"
              style={{
                margin: isMobile ? "12px -16px -16px" : "12px -24px -24px",
                borderRadius: "0 0 8px 8px",
              }}
            />
          </div>
        )}
      </section>

      <div
        style={{
          fontSize: 12,
          color: C.textLight,
          marginTop: 16,
          lineHeight: 1.5,
        }}
      >
        Official GST tax invoices are generated for all transactions. Click any
        payment row or use its download button to view and print your invoice.
      </div>

      {selectedPayment && (
        <PaymentDetailsModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
          onViewInvoice={(p) => {
            setSelectedPayment(null);
            setSelectedInvoice(p);
          }}
        />
      )}

      {selectedInvoice && (
        <TransactionInvoiceModal
          payment={selectedInvoice}
          user={data?.user}
          onClose={() => setSelectedInvoice(null)}
          onShowToast={setToast}
        />
      )}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </AppShell>
  );
}

// ---------- subcomponents ----------

function MembershipCard({
  membership,
  loading,
  onPay,
  paying,
  payError,
  isMobile,
  onDownloadInvoice,
}) {
  if (loading) {
    return (
      <section
        style={{ ...cardStyle, padding: isMobile ? 16 : 24, marginBottom: 16 }}
      >
        <div style={{ fontSize: 13, color: C.textMed }}>
          Loading membership status...
        </div>
      </section>
    );
  }

  // ===== PROFESSIONAL PAYMENT PROMPT & WARNINGS DISABLED -- see RE-ENABLE note below =====
  // Disabled on: 2026-09-12. Reason: Professional membership payment bypassed / complimentary access.
  // To re-enable: uncomment the original status mapping, renewal warnings, and payment button below.
  // Also see matching commented blocks in:
  //   - packages/backend/src/identity-verification/identity-verification.service.ts
  //   - packages/frontend/src/pages/Register.jsx
  //   - docs/TEMPORARY_CHANGES.md (full restoration checklist)
  // const status = membership?.status ?? "NONE";
  // const styles = {
  //   NONE: { bg: C.bgMuted, color: C.textMed, label: "No active membership" },
  //   ACTIVE: { bg: C.greenLight, color: C.green, label: "Active" },
  //   GRACE_PERIOD: { bg: C.amberLight, color: C.amber, label: "Grace period" },
  //   EXPIRED: { bg: C.redLight, color: C.red, label: "Expired" },
  // }[status] ?? { bg: C.bgMuted, color: C.textMed, label: status };
  //
  // const expiresInDays = membership?.expiresAt
  //   ? Math.ceil(
  //       (new Date(membership.expiresAt).getTime() - Date.now()) /
  //         (24 * 60 * 60 * 1000),
  //     )
  //   : null;
  // const showRenewWarning =
  //   status === "ACTIVE" && expiresInDays !== null && expiresInDays <= 30;
  // const needsRenewal =
  //   status === "NONE" ||
  //   status === "EXPIRED" ||
  //   status === "GRACE_PERIOD" ||
  //   showRenewWarning;
  // ===== END DISABLED BLOCK =====

  // ACTIVE BYPASS LOGIC: Membership is always displayed as "Active (complimentary)"
  const styles = {
    bg: C.greenLight,
    color: C.green,
    label: "Active (complimentary)",
  };

  return (
    <section
      style={{ ...cardStyle, padding: isMobile ? 16 : 24, marginBottom: 16 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 12,
          gap: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>
            Membership
          </h2>
          <div
            style={{
              display: "flex",
              alignItems: isMobile ? "flex-start" : "center",
              flexDirection: isMobile ? "column" : "row",
              gap: 8,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "2px 10px",
                fontSize: 11,
                fontWeight: 700,
                color: styles.color,
                background: styles.bg,
                borderRadius: 10,
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: styles.color,
                }}
              />
              {styles.label}
            </span>
            {membership?.expiresAt && (
              <span style={{ fontSize: 13, color: C.textMed }}>
                Valid through {formatDate(membership.expiresAt)}
              </span>
            )}
          </div>
          {/* Renewal and expiration warnings suppressed while payment is bypassed */}
        </div>

        {/* ===== PAYMENT BUTTON DISABLED -- see RE-ENABLE note below ===== */}
        {/* {needsRenewal && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              alignItems: "flex-end",
            }}
          >
            <button
              onClick={() => onPay("CASHFREE")}
              disabled={paying}
              style={{
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                color: "white",
                background: paying ? C.textLight : C.green,
                border: 0,
                borderRadius: 4,
                cursor: paying ? "default" : "pointer",
                fontFamily: font,
                whiteSpace: "nowrap",
              }}
            >
              {paying
                ? "Opening..."
                : `Pay ${formatRupees(PRICES.PROFESSIONAL_MEMBERSHIP)} / year`}
            </button>
          </div>
        )} */}
        {/* ===== END DISABLED BLOCK ===== */}

        {/* Annual Subscription Invoice Download */}
        <button
          onClick={onDownloadInvoice}
          title="Download Annual Subscription Invoice"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            background: "#f0fdf4",
            color: "#16a34a",
            border: "1.5px solid #86efac",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: font,
            flexShrink: 0,
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#16a34a";
            e.currentTarget.style.color = "white";
            e.currentTarget.style.borderColor = "#16a34a";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#f0fdf4";
            e.currentTarget.style.color = "#16a34a";
            e.currentTarget.style.borderColor = "#86efac";
          }}
        >
          <svg width="13" height="13" viewBox="0 0 384 512" fill="currentColor">
            <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v306.8L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
          </svg>
          Annual Invoice
        </button>
      </div>

      {payError && (
        <div
          style={{
            fontSize: 12,
            color: C.red,
            padding: "8px 12px",
            background: C.redLight,
            borderRadius: 4,
          }}
        >
          {payError}
        </div>
      )}

      <div
        style={{
          fontSize: 12,
          color: C.textLight,
          marginTop: 8,
          lineHeight: 1.4,
        }}
      >
        Your complimentary membership unlocks the ability to build a verified
        profile, request verifications, and share your profile publicly.
      </div>
    </section>
  );
}

function PaymentRow({
  payment,
  showDivider,
  onClick,
  onOpenInvoice,
  isMobile,
}) {
  const [hover, setHover] = useState(false);
  const statusStyle = {
    CAPTURED: { color: C.green, label: "Paid" },
    COUPON_REDEEMED: { color: C.green, label: "Coupon Redeemed" },
    CREATED: { color: C.amber, label: "Pending" },
    FAILED: { color: C.red, label: "Failed" },
    REFUNDED: { color: C.textMed, label: "Refunded" },
    REFUND_PENDING: { color: C.amber, label: "Refund pending" },
    AUTHORIZED: { color: C.amber, label: "Authorised" },
  }[payment.status] ?? { color: C.textMed, label: payment.status };

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "12px 16px",
        margin: "0 -16px",
        borderTop: showDivider ? `1px solid ${C.divider}` : "none",
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "center",
        cursor: "pointer",
        background: hover ? C.bgMuted : "transparent",
        borderRadius: 6,
        transition: "background 0.12s ease",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.textDark }}>
          {payment.description}
        </div>
        <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
          {formatDate(payment.paidAt ?? payment.createdAt)}
          {(payment.providerOrderId ||
            payment.providerPaymentId ||
            payment.razorpayPaymentId) && (
            <>
              {" "}
              - Ref:{" "}
              {(
                payment.providerOrderId ||
                payment.providerPaymentId ||
                payment.razorpayPaymentId
              ).slice(0, 20)}
            </>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 10 : 16,
          flexShrink: 0,
        }}
      >
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: C.textDark,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatRupees(payment.amountPaise)}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: statusStyle.color,
              marginTop: 2,
            }}
          >
            {statusStyle.label}
          </div>
        </div>

        {payment.provider !== "COUPON" &&
          payment.status !== "COUPON_REDEEMED" &&
          (payment.amountPaise > 0 || payment.invoiceId) && (
            <button
              className="Btn"
              onClick={(e) => {
                e.stopPropagation();
                onOpenInvoice(e);
              }}
              title="Download GST Tax Invoice"
            >
              <svg
                className="svgIcon"
                viewBox="0 0 384 512"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v306.8L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"></path>
              </svg>
              <span className="icon2"></span>
            </button>
          )}
      </div>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  function handleCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      onClick={handleCopy}
      style={{
        background: "none",
        border: 0,
        color: copied ? C.green : C.textLight,
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 600,
        marginLeft: 8,
        padding: "2px 6px",
        borderRadius: 4,
        backgroundColor: copied ? C.greenLight : C.bgMuted,
        transition: "all 0.15s ease",
        fontFamily: font,
      }}
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function PaymentDetailsModal({ payment, onClose, onViewInvoice }) {
  const isMobile = useIsMobile();
  const statusStyle = {
    CAPTURED: { color: C.green, bg: C.greenLight, label: "Paid" },
    COUPON_REDEEMED: {
      color: C.green,
      bg: C.greenLight,
      label: "Coupon Redeemed",
    },
    CREATED: { color: C.amber, bg: C.amberLight, label: "Pending" },
    FAILED: { color: C.red, bg: C.redLight, label: "Failed" },
    REFUNDED: { color: C.textMed, bg: C.bgMuted, label: "Refunded" },
    REFUND_PENDING: {
      color: C.amber,
      bg: C.amberLight,
      label: "Refund pending",
    },
    AUTHORIZED: { color: C.amber, bg: C.amberLight, label: "Authorised" },
  }[payment.status] ?? {
    color: C.textMed,
    bg: C.bgMuted,
    label: payment.status,
  };

  const dateStr =
    payment.paidAt || payment.createdAt
      ? new Date(payment.paidAt || payment.createdAt).toLocaleString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "N/A";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(22, 41, 76, 0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: isMobile ? 16 : 0,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: C.bg,
          border: `1.5px solid ${C.textDark}`,
          borderRadius: 12,
          padding: isMobile ? 16 : 28,
          width: "100%",
          maxWidth: 480,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2
            style={{
              fontSize: 16,
              fontWeight: 700,
              margin: 0,
              color: C.textDark,
            }}
          >
            Payment Transaction Details
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: 0,
              fontSize: 22,
              fontWeight: "600",
              color: C.textLight,
              cursor: "pointer",
              padding: "0 4px",
              lineHeight: 1,
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>

        <div
          style={{
            background: statusStyle.bg,
            borderRadius: 8,
            padding: "16px",
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: C.textMed,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            Transaction Status
          </div>
          <div
            style={{ fontSize: 20, fontWeight: 800, color: statusStyle.color }}
          >
            {statusStyle.label}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            marginBottom: 24,
          }}
        >
          <ModalInfoRow label="Description" value={payment.description} />
          <ModalInfoRow
            label="Amount Paid"
            value={formatRupees(payment.amountPaise)}
            highlight={payment.status === "CAPTURED"}
          />
          <ModalInfoRow label="Transaction Date" value={dateStr} />
          <ModalInfoRow
            label="Gateway Provider"
            value={
              payment.provider === "COUPON"
                ? "None (100% Coupon Redeemed)"
                : payment.provider || "CASHFREE"
            }
          />

          <ModalInfoRow
            label="Internal Payment ID"
            value={payment.id}
            action={<CopyButton text={payment.id} />}
          />

          {payment.providerOrderId && (
            <ModalInfoRow
              label="Gateway Order ID"
              value={payment.providerOrderId}
              action={<CopyButton text={payment.providerOrderId} />}
            />
          )}

          {payment.providerPaymentId && (
            <ModalInfoRow
              label="Gateway Payment ID"
              value={payment.providerPaymentId}
              action={<CopyButton text={payment.providerPaymentId} />}
            />
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 10,
          }}
        >
          {payment.provider !== "COUPON" &&
            payment.status !== "COUPON_REDEEMED" &&
            (payment.amountPaise > 0 || payment.invoiceId) && (
              <button
                onClick={() => onViewInvoice(payment)}
                style={{
                  flex: 1,
                  padding: "12px",
                  minHeight: 44,
                  background: C.green,
                  color: "white",
                  border: 0,
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: font,
                  transition: "opacity 0.15s ease",
                }}
              >
                View Tax Invoice
              </button>
            )}
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "12px",
              minHeight: 44,
              background: C.navy,
              color: "white",
              border: 0,
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: font,
              transition: "opacity 0.15s ease",
            }}
          >
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
}

function TransactionInvoiceModal({ payment, user, onClose, onShowToast }) {
  const isMobile = useIsMobile();
  const invNo = `TAP-INV-${payment.id.slice(-8).toUpperCase()}`;
  const paidDate = formatDate(payment.paidAt || payment.createdAt);
  const [resending, setResending] = useState(false);

  const totalAmountPaise = payment.amountPaise;
  const convenienceFeePaise =
    payment.notes?.convenienceFeePaise ||
    (payment.notes?.orderAmountPaise &&
    payment.amountPaise > payment.notes.orderAmountPaise
      ? payment.amountPaise - payment.notes.orderAmountPaise
      : 0);

  const baseAmountPaise = totalAmountPaise - convenienceFeePaise;
  const baseTaxablePaise = Math.round((baseAmountPaise * 100) / 118);
  const taxablePaise = Math.round((totalAmountPaise * 100) / 118);
  const feeTaxablePaise = taxablePaise - baseTaxablePaise;
  const taxPaise = totalAmountPaise - taxablePaise;
  const cgstPaise = Math.round(taxPaise / 2);
  const sgstPaise = taxPaise - cgstPaise;

  const badgeStyle = {
    CAPTURED: { bg: C.greenLight, color: C.green, label: "PAID" },
    CREATED: { bg: C.amberLight, color: C.amber, label: "PENDING" },
    FAILED: { bg: C.redLight, color: C.red, label: "FAILED" },
    REFUNDED: { bg: C.bgMuted, color: C.textMed, label: "REFUNDED" },
    REFUND_PENDING: {
      bg: C.amberLight,
      color: C.amber,
      label: "REFUND PENDING",
    },
    AUTHORIZED: { bg: C.amberLight, color: C.amber, label: "AUTHORISED" },
  }[payment.status] ?? {
    bg: C.greenLight,
    color: C.green,
    label: payment.status,
  };

  const handlePrint = () => {
    window.print();
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const res = await retryInvoiceEmail(payment.id);
      if (res.data?.sent || res.data?.duplicatePrevented) {
        onShowToast?.({
          type: "success",
          title: "Tax Invoice Email Sent!",
          message: `Official Tax Invoice #${invNo} was successfully sent to your registered email.`,
        });
      } else {
        onShowToast?.({
          type: "error",
          title: "Email Delivery Failed",
          message:
            res.data?.error ||
            "Failed to dispatch email. Please check server SMTP configuration.",
        });
      }
    } catch (err) {
      onShowToast?.({
        type: "error",
        title: "Resend Error",
        message: err.message || "Failed to trigger email resend.",
      });
    } finally {
      setResending(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      onShowToast?.({
        type: "info",
        title: "Downloading Invoice PDF",
        message: `Preparing Tax Invoice #${invNo}.pdf...`,
        duration: 2500,
      });
      await downloadInvoicePdf(payment.id, invNo);
      onShowToast?.({
        type: "success",
        title: "Download Complete",
        message: `Invoice #${invNo}.pdf has been saved to your device.`,
        duration: 4000,
      });
    } catch (err) {
      onShowToast?.({
        type: "error",
        title: "Download Failed",
        message: err.message || "Could not download PDF invoice.",
      });
    }
  };

  return (
    <div
      className="invoice-modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.75)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: isMobile ? 12 : 16,
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .invoice-printable, .invoice-printable * {
            visibility: visible;
          }
          .invoice-printable {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 24px;
            box-shadow: none !important;
            border: none !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div
        className="invoice-printable"
        style={{
          background: "white",
          borderRadius: 12,
          padding: isMobile ? 16 : 36,
          width: "100%",
          maxWidth: 680,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          color: "#1e293b",
          fontFamily: font,
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Actions header (No print) */}
        <div
          className="no-print"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: C.textLight }}>
            Tax Invoice Preview
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {payment.status === "CAPTURED" && (
              <button
                onClick={handleResendEmail}
                disabled={resending}
                style={{
                  padding: "8px 14px",
                  background: C.blue,
                  color: "white",
                  border: 0,
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: resending ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: resending ? 0.7 : 1,
                }}
              >
                {resending ? "Sending..." : "Resend Email"}
              </button>
            )}
            <button
              onClick={handleDownloadPdf}
              style={{
                padding: "8px 14px",
                background: C.green,
                color: "white",
                border: 0,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Download PDF
            </button>
            <button
              onClick={handlePrint}
              style={{
                padding: "8px 14px",
                background: C.bgMuted,
                color: C.textDark,
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Print
            </button>
            <button
              onClick={onClose}
              style={{
                padding: "8px 14px",
                background: C.bgMuted,
                color: C.textDark,
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Invoice Header */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "flex-start",
            gap: isMobile ? 16 : 0,
            marginBottom: isMobile ? 20 : 32,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  background: C.navy,
                  color: "white",
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: 14,
                }}
              >
                T
              </div>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: C.navy,
                  letterSpacing: -0.5,
                }}
              >
                TickAndPass
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
              <strong>TickAndPass Technologies Pvt. Ltd.</strong>
              <br />
              No. 42, OMR IT Highway Corridor
              <br />
              Chennai, Tamil Nadu 600096, India
              <br />
              <strong>GSTIN:</strong> 33AAAAA0000A1Z5 | <strong>PAN:</strong>{" "}
              AAAAA0000A
              <br />
              Email: support@tickandpass.com
            </div>
          </div>
          <div
            style={{
              textAlign: isMobile ? "left" : "right",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: C.navy,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              TAX INVOICE
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#334155",
                marginTop: 4,
              }}
            >
              #{invNo}
            </div>
            <div style={{ marginTop: 8 }}>
              <span
                style={{
                  background: badgeStyle.bg,
                  color: badgeStyle.color,
                  padding: "3px 10px",
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                }}
              >
                {badgeStyle.label}
              </span>
            </div>
          </div>
        </div>

        {/* Billed To & Metadata */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? 16 : 24,
            padding: isMobile ? "12px 14px" : "16px 20px",
            background: "#f8fafc",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            marginBottom: 28,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              Billed To (Customer)
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              {user?.email ?? payment.payerUserId ?? "Valued Customer"}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              Account Type: Professional / Individual
              <br />
              Place of Supply: Tamil Nadu (33)
            </div>
          </div>

          <div style={{ textAlign: isMobile ? "left" : "right" }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              Payment Information
            </div>
            <div style={{ fontSize: 12, color: "#334155" }}>
              <strong>Date:</strong> {paidDate}
              <br />
              <strong>Gateway:</strong> {payment.provider || "RAZORPAY"}
              <br />
              <strong>Transaction Ref:</strong>{" "}
              {(payment.providerPaymentId || payment.id).slice(0, 20)}
            </div>
          </div>
        </div>

        {/* Itemized Table */}
        <div
          data-scrolltable
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            marginBottom: 24,
          }}
        >
          <table
            style={{
              width: "100%",
              minWidth: isMobile ? 480 : "100%",
              borderCollapse: "collapse",
              fontSize: 12,
            }}
          >
            <thead>
              <tr
                style={{
                  background: C.navy,
                  color: "white",
                  textAlign: "left",
                }}
              >
                <th
                  style={{ padding: "10px 12px", borderRadius: "6px 0 0 6px" }}
                >
                  Description
                </th>
                <th style={{ padding: "10px 12px" }}>SAC Code</th>
                <th style={{ padding: "10px 12px", textAlign: "center" }}>
                  Qty
                </th>
                <th style={{ padding: "10px 12px", textAlign: "right" }}>
                  Taxable Amt
                </th>
                <th
                  style={{
                    padding: "10px 12px",
                    textAlign: "right",
                    borderRadius: "0 6px 6px 0",
                  }}
                >
                  Total (Rs)
                </th>
              </tr>
            </thead>
            <tbody>
              {convenienceFeePaise > 0 ? (
                <>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td
                      style={{
                        padding: "12px",
                        fontWeight: 600,
                        color: "#0f172a",
                      }}
                    >
                      {payment.description}
                      <div
                        style={{
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 400,
                        }}
                      >
                        Standard Subscription / Service Fee
                      </div>
                    </td>
                    <td style={{ padding: "12px", color: "#64748b" }}>
                      998439
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>1</td>
                    <td
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatRupees(baseTaxablePaise)}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatRupees(baseTaxablePaise)}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td
                      style={{
                        padding: "12px",
                        fontWeight: 600,
                        color: "#0f172a",
                      }}
                    >
                      Payment Gateway Convenience Fee
                      <div
                        style={{
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 400,
                        }}
                      >
                        Gateway Processing & Service Surcharge
                      </div>
                    </td>
                    <td style={{ padding: "12px", color: "#64748b" }}>
                      998439
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>1</td>
                    <td
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatRupees(feeTaxablePaise)}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatRupees(feeTaxablePaise)}
                    </td>
                  </tr>
                </>
              ) : (
                <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td
                    style={{
                      padding: "12px",
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {payment.description}
                    <div
                      style={{
                        fontSize: 10,
                        color: "#64748b",
                        fontWeight: 400,
                      }}
                    >
                      Standard Subscription / Service Fee
                    </div>
                  </td>
                  <td style={{ padding: "12px", color: "#64748b" }}>998439</td>
                  <td style={{ padding: "12px", textAlign: "center" }}>1</td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatRupees(taxablePaise)}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "right",
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatRupees(taxablePaise)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Financial Summary Breakdown */}
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "stretch" : "flex-end",
            marginBottom: 28,
          }}
        >
          <div style={{ width: isMobile ? "100%" : 260, fontSize: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                color: "#64748b",
              }}
            >
              <span>Taxable Value:</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupees(taxablePaise)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                color: "#64748b",
              }}
            >
              <span>CGST (9%):</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupees(cgstPaise)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                color: "#64748b",
              }}
            >
              <span>SGST (9%):</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupees(sgstPaise)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 0 0",
                borderTop: "2px solid #0f172a",
                fontWeight: 800,
                fontSize: 14,
                color: C.navy,
              }}
            >
              <span>Grand Total:</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupees(totalAmountPaise)}
              </span>
            </div>
          </div>
        </div>

        {/* Notice & Footer */}
        <div
          style={{
            borderTop: "1px dashed #cbd5e1",
            paddingTop: 16,
            fontSize: 10,
            color: "#94a3b8",
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          This is a computer-generated tax invoice issued by TickAndPass
          Technologies Pvt. Ltd. and requires no physical signature.
          <br />
          For any queries regarding this invoice, please write to
          support@tickandpass.com mentioning Invoice #{invNo}.
        </div>
      </div>
    </div>
  );
}

function ModalInfoRow({ label, value, highlight, action }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.divider}`, paddingBottom: 8 }}>
      <div
        style={{
          fontSize: 11,
          color: C.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: highlight ? 700 : 600,
            color: highlight ? C.green : C.textDark,
            wordBreak: "break-all",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </span>
        {action}
      </div>
    </div>
  );
}

// ---------- helpers ----------

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 24,
};
